/*
 * Copyright © Scale Microgrid Solutions Operating, LLC [2023].
 * All rights reserved.
 *
 * SPDX-FileCopyrightText: ©2023 Scale Microgrid Solutions Operating, LLC <legal@scalemicrogrids.com>
 */

import React from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import dayjs from "dayjs";
import PropTypes from "prop-types";

const TariffCard = ({
  config,
  label,
  tariffDetails,
  totalPropertiesCount,
  displayNameMap = {},
  isLoading = false,
  showLabel = false,
  hasSiblingCard = false,
  children,
}) => {
  if (!config) return null;

  if (isLoading) {
    return (
      <Box
        sx={{
          border: "1px solid #E3E3E3",
          borderRadius: "8px",
          padding: "1.5rem",
          flex: 1,
          minWidth: hasSiblingCard ? "45%" : "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
        }}
      >
        <CircularProgress size={30} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        padding: "1.5rem",
        flex: 1,
        minWidth: hasSiblingCard ? "45%" : "100%",
        backgroundColor: "#FAFAFA",
      }}
    >
      {showLabel && (
        <Typography
          sx={{
            fontSize: "12px",
            color: "text.secondary",
            lineHeight: "12px",
            marginBottom: "0.5rem",
          }}
        >
          {label}
        </Typography>
      )}
      {tariffDetails?.tariffCode && (
        <Typography
          sx={{
            color: "text.primary",
            marginBottom: "0.25rem",
          }}
          variant="h6"
        >
          {tariffDetails.tariffCode}
        </Typography>
      )}
      {tariffDetails?.tariffName && (
        <Typography
          sx={{
            color: "text.primary",
          }}
          variant="subtitle2"
        >
          {tariffDetails.tariffName}
        </Typography>
      )}
      {tariffDetails?.effectiveDate && (
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
          }}
        >
          {dayjs(tariffDetails.effectiveDate).format("MMM D, YYYY")}
        </Typography>
      )}

      <Box sx={{ marginTop: "1.5rem" }}>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
          }}
        >
          {totalPropertiesCount} tariff{" "}
          {totalPropertiesCount === 1 ? "property" : "properties"} loaded
        </Typography>
        {children ? (
          <Box sx={{ marginTop: "1rem" }}>{children}</Box>
        ) : (
          <>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                marginTop: "1rem",
              }}
            >
              {config.tariff_properties?.length || 0}{" "}
              {config.tariff_properties?.length === 1 ? "override" : "overrides"}:
            </Typography>
            {config.tariff_properties && config.tariff_properties.length > 0 && (
              <Box component="ul" sx={{ margin: 0, paddingLeft: "1.5rem" }}>
                {config.tariff_properties.map((prop, index) => {
                  const displayName = displayNameMap[prop.key_name] || prop.key_name;
                  return (
                    <Typography
                      key={index}
                      component="li"
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                      }}
                    >
                      {displayName}: {prop.data_value}
                    </Typography>
                  );
                })}
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

TariffCard.propTypes = {
  config: PropTypes.shape({
    config_id: PropTypes.number,
    master_tariff_id: PropTypes.number,
    bill_type: PropTypes.string,
    lse_name: PropTypes.string,
    option: PropTypes.string,
    tariff_properties: PropTypes.arrayOf(
      PropTypes.shape({
        key_name: PropTypes.string,
        data_value: PropTypes.string,
      })
    ),
  }),
  label: PropTypes.string,
  tariffDetails: PropTypes.shape({
    tariffCode: PropTypes.string,
    tariffName: PropTypes.string,
    lseId: PropTypes.number,
    effectiveDate: PropTypes.string,
    endDate: PropTypes.string,
  }),
  totalPropertiesCount: PropTypes.number,
  displayNameMap: PropTypes.object,
  isLoading: PropTypes.bool,
  showLabel: PropTypes.bool,
  hasSiblingCard: PropTypes.bool,
  children: PropTypes.node,
};

export default TariffCard;
